import {useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Stack
} from "@mui/material";
import * as React from "react";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {useQuery} from "react-query";
import {ProfileClusterService} from "crqlar-react-common/dist/services/profile/profileCluster.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import Property from "crqlar-react-common/dist/components/Property";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import Button from "@mui/material/Button";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {ClusterType, ClusterAction} from "crqlar-react-common/dist/models/profile/profileCluster.model";
import SettingsPageHeader from "../layout/SettingsPageHeader";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {formatProfileListName, getPrimaryEmail} from "crqlar-react-common/dist/utils/profile.util";

interface ClusterProfileEntry {
    profileId: string
    profile?: Profile
}

const ProfileClusterDetails = () => {

    useTrackScreenVisit('system profile cluster details')

    const {t} = useTranslation()
    const {id} = useParams()
    const navigate = useNavigate()

    const clusterQuery = useQuery(['profileCluster', id], async () => {
        if (id && id !== 'new') {
            return ProfileClusterService.getProfileClusterById(id)
        }
        return null
    })

    const cluster = clusterQuery.data

    const profilesQuery = useQuery(
        ['clusterProfiles', cluster?.profileIds],
        async () => {
            if (!cluster?.profileIds?.length) return []

            return await Promise.all(
                cluster.profileIds.map(async (profileId): Promise<ClusterProfileEntry> => {
                    const profile = await ProfileService.getProfileById(profileId) as Profile | undefined
                    return {profileId, profile}
                })
            )
        },
        {
            enabled: !!cluster?.profileIds?.length
        }
    )

    const getClusterTypeColor = (type?: ClusterType): "success" | "warning" | "error" | "default" => {
        switch (type) {
            case ClusterType.DEFINITE_MERGE:
                return "success"
            case ClusterType.PROBABLE_MERGE:
                return "warning"
            case ClusterType.NEEDS_REVIEW:
                return "error"
            case ClusterType.SINGLETON:
            default:
                return "default"
        }
    }

    const getActionColor = (action?: ClusterAction): "success" | "warning" | "error" | "default" => {
        switch (action) {
            case ClusterAction.AUTO_MERGE:
                return "success"
            case ClusterAction.REVIEW_BEFORE_MERGE:
                return "warning"
            case ClusterAction.MANUAL_REVIEW:
                return "error"
            case ClusterAction.NO_ACTION:
            default:
                return "default"
        }
    }

    if (clusterQuery.isLoading) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                <SettingsPageHeader
                    title={t('Profile Cluster')}
                    back={() => navigate('/system/profileClusters')}
                    loading
                />
                <Stack sx={{width: '100%', maxWidth: '900px', mx: 'auto', mt: 3}}>
                    <Typography>{t('Loading...')}</Typography>
                </Stack>
            </Box>
        )
    }

    if (!cluster) {
        return (
            <Box sx={{width: '100%', pb: 8}}>
                <SettingsPageHeader
                    title={undefined}
                    back={() => navigate('/system/profileClusters')}
                />
                <Stack sx={{width: '100%', maxWidth: '900px', mx: 'auto', mt: 3}}>
                    <Typography>{t('Cluster not found')}</Typography>
                </Stack>
            </Box>
        )
    }

    const profileEntries = profilesQuery.data || []
    const missingProfileIds = profileEntries
        .filter(({profile}) => !profile)
        .map(({profileId}) => profileId)

    const basicInfoSection = (
        <Card variant="outlined">
            <CardHeader
                title={t('Cluster Information')}
                titleTypographyProps={{variant: 'h6'}}
            />
            <CardContent>
                <Property title={t('Cluster ID')}>
                    <Typography>{cluster.clusterId}</Typography>
                </Property>

                <Divider/>

                <Property title={t('Cluster Type')}>
                    <Chip
                        label={cluster.clusterType || '-'}
                        color={getClusterTypeColor(cluster.clusterType)}
                    />
                </Property>

                <Divider/>

                <Property title={t('Size')}>
                    <Typography>{cluster.size}</Typography>
                </Property>

                <Divider/>

                <Property title={t('Coherence Score')}>
                    <Typography>{cluster.coherenceScore?.toFixed(2)}</Typography>
                </Property>

                <Divider/>

                <Property title={t('Min Pairwise Similarity')}>
                    <Typography>{cluster.minPairwiseSimilarity?.toFixed(2)}</Typography>
                </Property>

                <Divider/>

                <Property title={t('Avg Pairwise Similarity')}>
                    <Typography>{cluster.avgPairwiseSimilarity?.toFixed(2)}</Typography>
                </Property>

                <Divider/>

                <Property title={t('Suggested Action')}>
                    <Chip
                        label={cluster.suggestedAction || '-'}
                        color={getActionColor(cluster.suggestedAction)}
                    />
                </Property>

                <Divider/>

                <Property title={t('Formation Reason')}>
                    <Typography>{cluster.formationReason}</Typography>
                </Property>

                {cluster.representativeProfileId && (
                    <>
                        <Divider/>
                        <Property title={t('Representative Profile')}>
                            <Button
                                endIcon={<OpenInNewIcon/>}
                                onClick={() => navigate(`/system/profiles/${cluster.representativeProfileId}`)}
                            >
                                {t('View')}
                            </Button>
                        </Property>
                    </>
                )}
            </CardContent>
        </Card>
    )

    const profilesSection = (
        <Card variant="outlined">
            <CardHeader
                title={`${t('Profiles in Cluster')} (${cluster.profileIds?.length || 0})`}
                titleTypographyProps={{variant: 'h6'}}
            />
            <CardContent>
                {profilesQuery.isLoading ? (
                    <Typography>{t('Loading profiles...')}</Typography>
                ) : profilesQuery.isError ? (
                    <Alert severity="error" variant="outlined">
                        {t('Profiles could not be loaded.')}
                    </Alert>
                ) : (
                    <Stack gap={1}>
                        {missingProfileIds.length > 0 && (
                            <Alert severity="warning" variant="outlined">
                                {t('Some profiles referenced by this cluster could not be loaded.')}
                            </Alert>
                        )}

                        {profileEntries.map(({profileId, profile}) => {
                            if (!profile) {
                                return (
                                    <Box
                                        key={profileId}
                                        sx={{
                                            p: 1.5,
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                        }}
                                    >
                                        <Typography color="text.secondary">
                                            {t('Missing profile')}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {profileId}
                                        </Typography>
                                    </Box>
                                )
                            }

                            const isRepresentative = profile.id === cluster.representativeProfileId

                            return (
                                <Box
                                    key={profileId}
                                    sx={{
                                        p: 1.5,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        bgcolor: isRepresentative ? 'action.hover' : 'transparent',
                                    }}
                                >
                                    <Stack
                                        direction={'row'}
                                        alignItems={'center'}
                                        justifyContent={'space-between'}
                                    >
                                        <Stack>
                                            <Typography fontWeight={isRepresentative ? 'bold' : 'normal'}>
                                                {formatProfileListName(profile)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {getPrimaryEmail(profile) || '-'}
                                            </Typography>
                                            {isRepresentative && (
                                                <Chip
                                                    label={t('Representative')}
                                                    size="small"
                                                    color="primary"
                                                    sx={{mt: 0.5, width: 'fit-content'}}
                                                />
                                            )}
                                        </Stack>
                                        <Button
                                            endIcon={<OpenInNewIcon/>}
                                            onClick={() => navigate(`/system/profiles/${profile.id}`)}
                                        >
                                            {t('View')}
                                        </Button>
                                    </Stack>
                                </Box>
                            )
                        })}
                    </Stack>
                )}
            </CardContent>
        </Card>
    )

    return (
        <Box sx={{width: '100%', pb: 8}}>
            <SettingsPageHeader
                title={cluster.clusterId || t('Profile Cluster')}
                back={() => navigate('/system/profileClusters')}
            />
            <Stack
                sx={{
                    width: '100%',
                    maxWidth: '900px',
                    gap: 2,
                    mx: 'auto',
                    mt: 3,
                }}
            >
                {cluster.clusterType === ClusterType.NEEDS_REVIEW && (
                    <Alert severity="warning" variant="outlined">
                        {t('This cluster needs manual review before any action can be taken.')}
                    </Alert>
                )}

                {cluster.suggestedAction === ClusterAction.AUTO_MERGE && (
                    <Alert severity="success" variant="outlined">
                        {t('This cluster is safe to merge automatically.')}
                    </Alert>
                )}

                {basicInfoSection}
                {profilesSection}
                <Divider/>
                <Typography variant={'body2'} color={'text.secondary'} textAlign={'start'}>
                    {t('Last updated')}: {cluster.updateDate?.toLocaleString() || '-'}
                </Typography>
            </Stack>
        </Box>
    );
}

export default ProfileClusterDetails;
